"use client";

import { useId, useRef, useState } from "react";
import "./AssumptionRolloutSchedule.css";

/** A caller-owned annual rollout percentage. Values use the 0 through 100 scale. */
export interface AssumptionRolloutPeriod {
  /** Stable identifier supplied by the scenario owner. */
  id: string;
  label: string;
  value: number;
}

export interface AssumptionRolloutScheduleProps {
  /** The currently active model horizon in years. */
  horizon: number;
  /** Quick-select horizon options. Defaults to 3, 5, and 7 years. */
  presets?: readonly number[];
  /** Annual periods to render. The component never creates periods itself. */
  periods: readonly AssumptionRolloutPeriod[];
  /** Smallest permitted custom horizon. Defaults to 1. */
  min?: number;
  /** Largest permitted custom horizon. Defaults to 15. */
  max?: number;
  /** Horizon to restore when the user clears a custom value. Defaults to presets[0] ?? min. */
  defaultHorizon?: number;
  /** Called with the newly selected active horizon. */
  onHorizonChange: (horizon: number) => void;
  /** Called with a period ID and its newly committed rollout percentage. */
  onRolloutChange: (periodId: string, percentage: number) => void;
}

function isWholeNumberInRange(
  value: number,
  min: number,
  max: number,
): boolean {
  return Number.isInteger(value) && value >= min && value <= max;
}

function parsePercentage(input: string): number | null {
  const normalized = input.trim();
  if (normalized === "") return null;

  const value = Number(normalized);
  return Number.isFinite(value) && value >= 0 && value <= 100 ? value : null;
}

/**
 * Controlled annual rollout editor for ROI assumptions.
 *
 * The component stores only in-progress input drafts. The parent owns the
 * horizon and period collection, so changing horizons cannot create or retain
 * rollout values for years the parent does not provide.
 */
export function AssumptionRolloutSchedule({
  horizon,
  presets = [3, 5, 7],
  periods,
  min = 1,
  max = 15,
  defaultHorizon,
  onHorizonChange,
  onRolloutChange,
}: AssumptionRolloutScheduleProps) {
  const idPrefix = `as-rollout-${useId()}`;
  const [editingPeriod, setEditingPeriod] = useState<string | null>(null);
  const [periodDraft, setPeriodDraft] = useState("");
  const [periodError, setPeriodError] = useState<string | null>(null);
  const [editingHorizon, setEditingHorizon] = useState(false);
  const [horizonDraft, setHorizonDraft] = useState("");
  const [horizonError, setHorizonError] = useState(false);
  const handledBlurRef = useRef(false);
  const resetHorizon = defaultHorizon ?? presets[0] ?? min;

  function beginPeriodEdit(periodId: string, value: number) {
    if (editingPeriod !== periodId) {
      setPeriodDraft(String(value));
      setPeriodError(null);
    }
    setEditingPeriod(periodId);
  }

  function commitPeriod(periodId: string): boolean {
    const percentage = parsePercentage(periodDraft);
    if (percentage === null) {
      setPeriodError(periodId);
      return false;
    }

    setEditingPeriod(null);
    setPeriodDraft("");
    setPeriodError(null);
    onRolloutChange(periodId, percentage);
    return true;
  }

  function cancelPeriodEdit() {
    setEditingPeriod(null);
    setPeriodDraft("");
    setPeriodError(null);
  }

  function beginHorizonEdit() {
    if (!editingHorizon) {
      setHorizonDraft(String(horizon));
      setHorizonError(false);
    }
    setEditingHorizon(true);
  }

  function commitHorizon(): boolean {
    const nextHorizon = Number(horizonDraft);
    if (!isWholeNumberInRange(nextHorizon, min, max)) {
      setHorizonError(true);
      return false;
    }

    setEditingHorizon(false);
    setHorizonDraft("");
    setHorizonError(false);
    if (nextHorizon !== horizon) onHorizonChange(nextHorizon);
    return true;
  }

  function cancelHorizonEdit() {
    setEditingHorizon(false);
    setHorizonDraft("");
    setHorizonError(false);
  }

  return (
    <section className="as-rollout" aria-labelledby={`${idPrefix}-title`}>
      <div className="as-rollout-header">
        <h2 id={`${idPrefix}-title`} className="as-rollout-title">
          Rollout
        </h2>
        <label className="as-rollout-years">
          <span>over</span>
          <select
            value={presets.includes(horizon) ? String(horizon) : "custom"}
            aria-label="Model horizon"
            onChange={(event) => {
              if (event.target.value === "custom") {
                beginHorizonEdit();
                return;
              }
              cancelHorizonEdit();
              onHorizonChange(Number(event.target.value));
            }}
          >
            {presets.map((preset) => (
              <option key={preset} value={preset}>
                {preset} years
              </option>
            ))}
            <option value="custom">Custom…</option>
          </select>
          {(editingHorizon || !presets.includes(horizon)) && (
            <span className="as-rollout-custom">
              <input
                type="text"
                inputMode="numeric"
                className="as-rollout-horizon-input"
                value={editingHorizon ? horizonDraft : String(horizon)}
                aria-invalid={horizonError || undefined}
                aria-describedby={
                  horizonError ? `${idPrefix}-horizon-error` : undefined
                }
                onFocus={beginHorizonEdit}
                onChange={(event) => setHorizonDraft(event.target.value)}
                onBlur={() => {
                  if (handledBlurRef.current) {
                    handledBlurRef.current = false;
                    return;
                  }
                  commitHorizon();
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    if (commitHorizon()) {
                      handledBlurRef.current = true;
                      event.currentTarget.blur();
                    }
                  } else if (event.key === "Escape") {
                    event.preventDefault();
                    handledBlurRef.current = true;
                    cancelHorizonEdit();
                    event.currentTarget.blur();
                  }
                }}
                aria-label={`Custom horizon, ${min} to ${max} years`}
                spellCheck={false}
              />
              <span className="as-rollout-custom-suffix">years</span>
              <button
                type="button"
                className="as-rollout-custom-reset"
                aria-label={`Return to ${resetHorizon} years`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  cancelHorizonEdit();
                  onHorizonChange(resetHorizon);
                }}
              >
                ×
              </button>
            </span>
          )}
        </label>
        {horizonError && (
          <p
            id={`${idPrefix}-horizon-error`}
            className="as-rollout-error"
            role="alert"
          >
            Enter a whole number from {min} to {max}.
          </p>
        )}
      </div>

      <div className="as-rollout-periods">
        {periods.map((period) => {
          const isEditing = editingPeriod === period.id;
          const hasError = periodError === period.id;
          const percentage = Math.max(0, Math.min(100, period.value));
          const inputId = `${idPrefix}-period-${period.id}`;
          const errorId = `${idPrefix}-period-${period.id}-error`;

          return (
            <div className="as-rollout-period" key={period.id}>
              <label className="as-rollout-period-label" htmlFor={inputId}>
                {period.label}
              </label>
              <div
                className="as-rollout-bar"
                role="progressbar"
                aria-label={`${period.label} rollout`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percentage}
              >
                <span
                  className="as-rollout-bar-fill"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <div className="as-rollout-value">
                <input
                  id={inputId}
                  type="text"
                  inputMode="numeric"
                  className="as-rollout-input"
                  value={isEditing ? periodDraft : String(period.value)}
                  aria-label={`${period.label} rollout percentage`}
                  aria-invalid={hasError || undefined}
                  aria-describedby={hasError ? errorId : undefined}
                  onFocus={() => beginPeriodEdit(period.id, period.value)}
                  onChange={(event) => setPeriodDraft(event.target.value)}
                  onBlur={() => {
                    if (handledBlurRef.current) {
                      handledBlurRef.current = false;
                      return;
                    }
                    commitPeriod(period.id);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      if (commitPeriod(period.id)) {
                        handledBlurRef.current = true;
                        event.currentTarget.blur();
                      }
                    } else if (
                      event.key === "ArrowUp" ||
                      event.key === "ArrowDown"
                    ) {
                      event.preventDefault();
                      const current =
                        parsePercentage(periodDraft) ?? period.value;
                      const next = Math.max(
                        0,
                        Math.min(
                          100,
                          current + (event.key === "ArrowUp" ? 1 : -1),
                        ),
                      );
                      setEditingPeriod(period.id);
                      setPeriodDraft(String(next));
                      setPeriodError(null);
                      handledBlurRef.current = true;
                      onRolloutChange(period.id, next);
                    } else if (event.key === "Escape") {
                      event.preventDefault();
                      handledBlurRef.current = true;
                      cancelPeriodEdit();
                      event.currentTarget.blur();
                    }
                  }}
                  spellCheck={false}
                />
                <span className="as-rollout-percent" aria-hidden="true">
                  %
                </span>
                {hasError && (
                  <p id={errorId} className="as-rollout-error" role="alert">
                    Enter a percentage from 0 to 100.
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
